import { isUndefined, omit, omitBy, uniq } from 'lodash'
import { fetchFlags } from 'requests/flags'
import {
  fetchPerformanceReviews,
  fetchPerformanceReview,
  putPerformanceReview,
} from 'requests/performanceReviews'
import { fetchCanvasser } from 'requests/canvassers'
import { fetchShifts } from 'requests/shifts'
import { FilterRule } from 'types/requests'
import {
  AssociatedFlag,
  AssociatedShift,
  EditablePerformanceReview,
  FetchCanvasserFlagsResponse,
  FetchPerformanceReviewResponse,
  FetchReviewReasonsResponse,
  FetchReviewsResponse,
  FlagFilters,
  PerformanceReview,
  PerformanceReviewFilters,
  PerformanceReviewFormData,
  SubmittablePerformanceReview,
  TabType,
} from './types'
import { TABS } from './constants'

export const getPerformanceReviewList = ({
  page,
  tab,
  filters = {},
}: {
  page: number
  tab: TabType
  filters: PerformanceReviewFilters
}) => {
  const rules = []

  if (tab === TABS.COMPLETED) {
    rules.push({
      column: 'status',
      operator: 'is',
      param: 'completed',
    })
  }

  if (tab === TABS.ACTIVE) {
    rules.push(
      filters.status
        ? {
            column: 'status',
            operator: 'is',
            param: filters.status,
          }
        : {
            column: 'status',
            operator: 'is',
            param: 'completed',
            invert: true,
          }
    )
  }

  if (filters.user) {
    rules.push({
      column: 'assigned_to_user_id',
      operator: 'is',
      param: filters.user.id,
    })
  }

  if (filters.canvasser) {
    rules.push({
      column: 'canvasser_id',
      operator: 'is',
      param: filters.canvasser.id,
    })
  }

  if (filters.turf) {
    rules.push({
      column: 'turf_id',
      operator: 'is',
      param: filters.turf,
    })
  }

  return fetchPerformanceReviews<FetchReviewsResponse>({
    fields: [
      'id',
      'status',
      'created_at',
      { canvasser: ['full_name'] },
      { requested_by_user: ['full_name', 'email'] },
      { assigned_to_user: ['full_name', 'email'] },
    ],
    filters: {
      rules,
    },
    current_page: page,
  })
}

export const fetchCanvasserFlags = (canvasserId: number, idQuery?: string) => {
  const rules: FilterRule[] = [
    {
      column: 'canvasser_id',
      operator: 'is',
      param: canvasserId,
    },
    {
      column: 'status',
      operator: 'is',
      param: 'pending',
      invert: true,
    },
  ]

  if (idQuery) {
    rules.push({
      column: 'id',
      operator: 'is',
      param: idQuery,
    })
  }
  return fetchFlags({
    fields: ['id'],
    filters: {
      rules,
    },
  }) as Promise<FetchCanvasserFlagsResponse>
}

export const fetchCanvasserReviewReasons = (canvasserId: number) =>
  fetchCanvasser(canvasserId, {
    fields: [{ turf: ['options'] }],
  }) as Promise<FetchReviewReasonsResponse>

export function getReviewReasonOptions(
  fetchResponse: FetchReviewReasonsResponse
) {
  return fetchResponse.canvasser.turf.options.canvasser_review_reasons.map(
    reason => ({ value: reason, label: reason })
  )
}

const PERFOMANCE_REVIEW_FIELDS = [
  'id',
  'reasons',
  'additional_details',
  'pull_from_field',
  'status',
  'created_at',
  'verification_start_date',
  'verification_end_date',
  'investigation_calls',
  'investigation_contacts',
  'investigation_result',
  'outcome',
  'notes',
  'requested_changes',
  'timeline_events',
  {
    canvasser: ['id', 'full_name', 'rolling_counts'],
  },
  { assigned_to_user: ['id', 'full_name', 'email'] },
  { requested_by_user: ['id', 'full_name', 'email'] },
  { created_by_user: ['full_name', 'email'] },
  { turf: ['name', 'options', 'voter_registration_enabled'] },
  { flags: ['id'] },
  { shifts: ['id'] },
] as const

export const getPerformanceReview = (reviewId: number) =>
  fetchPerformanceReview<FetchPerformanceReviewResponse>(reviewId, {
    fields: PERFOMANCE_REVIEW_FIELDS,
  })

export const updatePerformanceReview = (
  reviewId: number,
  attributes: SubmittablePerformanceReview
) =>
  putPerformanceReview<
    FetchPerformanceReviewResponse,
    SubmittablePerformanceReview
  >(
    reviewId,
    {
      fields: PERFOMANCE_REVIEW_FIELDS,
    },
    attributes
  )

const SHIFT_FIELDS = [
  'id',
  'name',
  'ready_for_qc_at',
  'status',
  { location: ['name'] },
  { flags: ['id'] },
] as const

export const getAssociatedShifts = (reviewId: number) =>
  fetchShifts<{ shifts: AssociatedShift[] }>({
    fields: SHIFT_FIELDS,
    filters: {
      rules: [
        {
          column: 'performance_reviews_id',
          operator: 'is',
          param: reviewId,
        },
      ],
    },
  })

export const searchShifts = (
  canvasserId: number,
  reviewId: number,
  nameQuery: string
) => {
  const rules: FilterRule[] = [
    {
      column: 'canvasser_id',
      operator: 'is',
      param: canvasserId,
    },
    {
      column: 'performance_reviews_id',
      operator: 'is',
      param: reviewId,
      invert: true,
    },
  ]

  if (nameQuery) {
    rules.push({
      column: 'name',
      operator: 'containing',
      param: nameQuery,
    })
  }

  return fetchShifts<{ shifts: AssociatedShift[] }>({
    fields: SHIFT_FIELDS,
    filters: {
      rules,
    },
  })
}

const FLAG_FIELDS = [
  'id',
  'status',
  'created_at',
  { trigger: ['name'] },
  {
    triggered_by_forms: [
      {
        shift: ['id', 'name'],
      },
    ],
  },
] as const

export const getAssociatedFlags = (reviewId: number) =>
  fetchFlags({
    fields: FLAG_FIELDS,
    filters: {
      rules: [
        {
          column: 'performance_reviews_id',
          operator: 'is',
          param: reviewId,
        },
      ],
    },
  }) as Promise<{ 'quality_control/flags': AssociatedFlag[] }>

export const searchFlags = (reviewId: number, filters: FlagFilters) => {
  const rules: FilterRule[] = [
    {
      column: 'performance_reviews_id',
      operator: 'is',
      param: reviewId,
      invert: true,
    },
  ]

  if (filters.start_date && filters.end_date) {
    rules.push({
      column: 'created_at',
      operator: 'between',
      param: [filters.start_date, filters.end_date],
    })
  }

  if (filters.flagId) {
    rules.push({
      column: 'id',
      operator: 'containing',
      param: filters.flagId,
    })
  }

  if (filters.type) {
    rules.push({
      column: 'trigger_resource_type',
      operator: 'is',
      param: filters.type,
    })
  }

  return fetchFlags({
    fields: FLAG_FIELDS,
    filters: {
      rules,
    },
  }) as Promise<{ 'quality_control/flags': AssociatedFlag[] }>
}

export function getFlagOptions(response: FetchCanvasserFlagsResponse) {
  return response['quality_control/flags'].map(({ id }) => ({
    label: `${id}`,
    value: id,
  }))
}

export function hasMissingFields<
  T extends Omit<PerformanceReviewFormData, 'flag_ids'>,
>(formData: T) {
  if (!formData.requested_by_user?.id) {
    return true
  }
  if (!formData.canvasser?.id) {
    return true
  }
  if (formData.pull_from_field === undefined) {
    return true
  }
  if (!formData.additional_details) {
    return true
  }
  if (!formData.reasons.length) {
    return true
  }
  return false
}

const parsePullFromFieldForSubmission = (
  value: 'true' | 'false' | undefined
) => {
  if (value === 'true') return true
  if (value === 'false') return false
  return value
}

const parsePullFromFieldForEdits = (value: boolean): 'true' | 'false' => {
  if (value === true) return 'true'
  return 'false'
}

export function formatForCreate(formData: PerformanceReviewFormData) {
  return {
    requested_by_user_id: formData.requested_by_user?.id,
    canvasser_id: formData.canvasser?.id,
    assigned_to_user_id: formData.assigned_to_user?.id,
    pull_from_field: parsePullFromFieldForSubmission(formData.pull_from_field),
    additional_details: formData.additional_details,
    flag_ids: formData.flag_ids,
    reasons: formData.reasons,
  }
}

export function formatForEdit(
  review: PerformanceReview
): EditablePerformanceReview {
  return {
    ...omit(review, [
      'pull_from_field',
      'created_by_user',
      'turf',
      'canvasser',
    ]),
    canvasser: { ...review.canvasser, turf: review.turf },
    pull_from_field: parsePullFromFieldForEdits(review.pull_from_field),
  }
}

export function formatForUpdate(
  review: EditablePerformanceReview
): SubmittablePerformanceReview {
  return omitBy(
    {
      ...omit(review, [
        'assigned_to_user',
        'requested_by_user',
        'canvasser',
        'pull_from_field',
      ]),
      assigned_to_user_id: review.assigned_to_user?.id,
      requested_by_user_id: review.requested_by_user.id,
      canvasser_id: review.canvasser.id,
      pull_from_field: parsePullFromFieldForSubmission(review.pull_from_field),
    },
    isUndefined
  )
}

export const getPacketNames = (flag: AssociatedFlag) =>
  uniq(flag.triggered_by_forms.map(form => form.shift.name)).join(', ')

const FLAG_STATUS_TO_URL_MAP = {
  ready: 'reviewed',
  reviewed: 'completed',
  completed: 'reopen',
} as const

export const navigateToFlag = (flag: AssociatedFlag) => {
  window.open(
    `/quality_control/flags/${FLAG_STATUS_TO_URL_MAP[flag.status]}/${flag.id}/start`,
    '_blank'
  )
}

export const navigateToPacket = (shift: AssociatedShift) => {
  window.open(`/collect/voter_registration/shifts/${shift.id}/packet`, '_blank')
}

export const getIdsForFlagsUpdate = (
  review: PerformanceReview,
  newFlags: AssociatedFlag[]
) => {
  const currentFlagIds = review.flags?.map(({ id }) => id) ?? []
  const newFlagIds = newFlags.map(({ id }) => id)

  const currentShiftIds = review.shifts?.map(({ id }) => id) ?? []
  const newShiftIds = newFlags.flatMap(flag =>
    flag.triggered_by_forms.map(form => form.shift.id)
  )

  return {
    flagIds: uniq([...currentFlagIds, ...newFlagIds]),
    shiftIds: uniq([...currentShiftIds, ...newShiftIds]),
  }
}

export const getIdsForShiftsUpdate = (
  review: PerformanceReview,
  newShifts: AssociatedShift[]
) => {
  const currentShiftIds = review.shifts?.map(({ id }) => id) ?? []
  const newShiftIds = newShifts.map(({ id }) => id)

  const currentFlagIds = review.flags?.map(({ id }) => id) ?? []
  const newFlagIds = newShifts.flatMap(shift =>
    shift.flags.map(flag => flag.id)
  )

  return {
    flagIds: uniq([...currentFlagIds, ...newFlagIds]),
    shiftIds: uniq([...currentShiftIds, ...newShiftIds]),
  }
}
